'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';
import {
  useLanguage,
  AVAILABLE_LOCALES,
  LOCALE_LABELS,
  LOCALE_SHORT,
  type UILocale,
} from '@/i18n/LanguageContext';
import { useTranslation } from '@/i18n/useTranslation';
import { useDashboardTheme } from '@/contexts/DashboardThemeContext';

export interface LanguageSelectorProps {
  placement?: 'down' | 'up';
  variant?: 'default' | 'ghost' | 'pill';
  size?: 'sm' | 'md';
  className?: string;
}

const DROPDOWN_WIDTH = 184;
const ITEM_HEIGHT = 40;
const FOOTER_HEIGHT = 38;

function DropdownPortal({
  anchorRect,
  isOpal,
  locale,
  onSelect,
  onClose,
  placement,
}: {
  anchorRect: DOMRect;
  isOpal: boolean;
  locale: UILocale;
  onSelect: (loc: UILocale) => void;
  onClose: () => void;
  placement: 'down' | 'up';
}) {
  const { t } = useTranslation();
  const dropRef = useRef<HTMLDivElement>(null);

  // Compute fixed position from anchor rect
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const estimatedHeight = AVAILABLE_LOCALES.length * ITEM_HEIGHT + FOOTER_HEIGHT + 16;

  // Right-align with trigger, ensure it stays in viewport
  let left = anchorRect.right - DROPDOWN_WIDTH;
  if (left < 8) left = 8;
  if (left + DROPDOWN_WIDTH > vw - 8) left = vw - DROPDOWN_WIDTH - 8;

  const spaceBelow = vh - anchorRect.bottom - 8;
  const spaceAbove = anchorRect.top - 8;
  const openUp = placement === 'up' || (spaceBelow < estimatedHeight && spaceAbove > spaceBelow);

  const topVal = openUp ? undefined : anchorRect.bottom + 4;
  const bottomVal = openUp ? vh - anchorRect.top + 4 : undefined;

  // Close on outside click (capture phase so it catches portal clicks outside)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Keyboard navigation within the list
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!dropRef.current) return;
    const buttons = Array.from(
      dropRef.current.querySelectorAll<HTMLButtonElement>('[role="option"]')
    );
    const idx = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      buttons[(idx + 1) % buttons.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      buttons[(idx - 1 + buttons.length) % buttons.length]?.focus();
    }
  }, []);

  // Auto-focus selected item (or first) on mount
  useEffect(() => {
    const btns = dropRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]');
    if (!btns) return;
    const selected = Array.from(btns).find((b) => b.getAttribute('aria-selected') === 'true');
    (selected ?? btns[0])?.focus();
  }, []);

  const surfaceBg = isOpal ? 'rgba(255,255,255,0.98)' : 'rgba(9,22,40,0.97)';
  const surfaceBorder = isOpal ? 'rgba(0,0,0,0.10)' : 'rgba(34,211,238,0.18)';
  const surfaceShadow = isOpal
    ? '0 8px 32px rgba(10,20,50,0.16), 0 2px 8px rgba(0,0,0,0.08)'
    : '0 8px 32px rgba(0,0,0,0.60), 0 0 0 1px rgba(34,211,238,0.10)';
  const footerBorder = isOpal ? 'rgba(0,0,0,0.07)' : 'rgba(34,211,238,0.10)';
  const footerColor = isOpal ? '#94a3b8' : '#4a5d72';

  return createPortal(
    <div
      ref={dropRef}
      role="listbox"
      aria-label={t('language.select_language')}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        top: topVal,
        bottom: bottomVal,
        left,
        width: DROPDOWN_WIDTH,
        borderRadius: 12,
        background: surfaceBg,
        border: `1px solid ${surfaceBorder}`,
        boxShadow: surfaceShadow,
        zIndex: 99999,
        overflow: 'hidden',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      <div style={{ padding: '6px 0' }}>
        {AVAILABLE_LOCALES.map((loc) => {
          const isSelected = loc === locale;
          const labelColor = isSelected ? '#00b4a0' : isOpal ? '#0f172a' : '#e2e8f0';
          const selectedBg = isOpal ? 'rgba(0,180,160,0.07)' : 'rgba(34,211,238,0.08)';
          const hoverBg = isOpal ? 'rgba(0,180,160,0.05)' : 'rgba(34,211,238,0.07)';

          return (
            <button
              key={loc}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(loc)}
              style={{
                width: '100%',
                padding: '9px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 13,
                fontWeight: isSelected ? 700 : 500,
                color: labelColor,
                background: isSelected ? selectedBg : 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = isSelected
                  ? selectedBg
                  : hoverBg;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = isSelected
                  ? selectedBg
                  : 'transparent';
              }}
              onFocus={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = isSelected
                  ? selectedBg
                  : hoverBg;
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = isSelected
                  ? selectedBg
                  : 'transparent';
              }}
            >
              <span>{LOCALE_LABELS[loc]}</span>
              {isSelected && <Check size={13} color="#00b4a0" />}
            </button>
          );
        })}
      </div>
      <div
        style={{
          padding: '8px 14px',
          borderTop: `1px solid ${footerBorder}`,
          fontSize: 10,
          color: footerColor,
          lineHeight: 1.4,
        }}
      >
        {t('language.languages_supported', { count: String(AVAILABLE_LOCALES.length) })}
      </div>
    </div>,
    document.body
  );
}

export default function LanguageSelector({
  placement = 'down',
  variant = 'default',
  size = 'md',
  className = '',
}: LanguageSelectorProps) {
  const { locale, setLocale } = useLanguage();
  const { t } = useTranslation();
  const { theme } = useDashboardTheme();
  const isOpal = theme === 'opal';
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Only render portal after hydration
  useEffect(() => setMounted(true), []);

  const handleToggle = useCallback(() => {
    if (triggerRef.current) {
      setAnchorRect(triggerRef.current.getBoundingClientRect());
    }
    setOpen((v) => !v);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    (next: UILocale) => {
      setLocale(next);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [setLocale]
  );

  // Keep anchor rect up to date while open (scroll / resize)
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current) {
        setAnchorRect(triggerRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener('scroll', update, { capture: true, passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const isGhost = variant === 'ghost';
  const isPill = variant === 'pill';
  const isSmall = size === 'sm';

  const triggerColor = isOpal ? '#0d9488' : '#00b4a0';
  const triggerBg = isGhost
    ? 'transparent'
    : isOpal
    ? 'rgba(0,180,160,0.07)'
    : 'rgba(0,180,160,0.09)';
  const triggerBorder = isGhost
    ? 'none'
    : `1px solid ${isOpal ? 'rgba(0,180,160,0.22)' : 'rgba(0,180,160,0.30)'}`;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} className={className}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('language.selector_label')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: isSmall ? 4 : 6,
          cursor: 'pointer',
          border: triggerBorder,
          borderRadius: isPill ? 20 : 10,
          padding: isSmall ? '4px 8px' : '6px 12px',
          fontSize: isSmall ? 11 : 12,
          fontWeight: 600,
          background: triggerBg,
          color: triggerColor,
          transition: 'background 0.15s, outline 0.1s',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          outline: 'none',
        }}
        onFocus={(e) => {
          e.currentTarget.style.outline = '2px solid rgba(0,180,160,0.45)';
          e.currentTarget.style.outlineOffset = '2px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = 'none';
        }}
      >
        <Globe size={isSmall ? 12 : 14} style={{ flexShrink: 0 }} />
        <span>{LOCALE_SHORT[locale]}</span>
        <ChevronDown
          size={isSmall ? 10 : 12}
          style={{
            flexShrink: 0,
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </button>

      {mounted && open && anchorRect && (
        <DropdownPortal
          anchorRect={anchorRect}
          isOpal={isOpal}
          locale={locale}
          onSelect={handleSelect}
          onClose={handleClose}
          placement={placement}
        />
      )}
    </div>
  );
}
